import React, { createContext, useState, useReducer, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../Constants/constant";

// Modern auth state management with useReducer
interface AuthState {
  loading: boolean;
  data: {
    username?: string;
    role?: string;
    group?: string;
    [key: string]: any;
  } | null;
  token: string | null;
  isAuthenticated: boolean;
}

type AuthAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_AUTH_SUCCESS'; payload: { data: any; token: string; group: string } }
  | { type: 'SET_AUTH_FAILURE' }
  | { type: 'LOGOUT' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_AUTH_SUCCESS':
      return {
        loading: false,
        data: { ...action.payload.data, role: action.payload.group, group: action.payload.group },
        token: action.payload.token,
        isAuthenticated: true
      };
    case 'SET_AUTH_FAILURE':
      return {
        loading: false,
        data: null,
        token: null,
        isAuthenticated: false
      };
    case 'LOGOUT':
      return {
        loading: false,
        data: null,
        token: null,
        isAuthenticated: false
      };
    default:
      return state;
  }
};

const initialAuthState: AuthState = {
  loading: true,
  data: null,
  token: null,
  isAuthenticated: false
};

export const authContext = createContext({});

const AuthProvider = ({ children }: any) => {
  const [auth, dispatch] = useReducer(authReducer, initialAuthState);
  const [computeId, setComputeId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>('');
  const [inputRefresh, setInputRefresh] = useState<number>(0);
  const [draftLca, setDraftLca] = useState<number | null>(null);
  const navigate = useNavigate();

  // Modern logout with proper cleanup
  const handleLogout = useCallback(() => {
    // Clear all auth-related storage
    localStorage.removeItem("token");
    localStorage.removeItem("username"); // Clean legacy data
    localStorage.removeItem("role"); // Clean legacy data
    localStorage.removeItem("email");
    
    // Update state
    dispatch({ type: 'LOGOUT' });
    
    // Navigate to login
    navigate('/');
  }, [navigate]);

  // Check for existing token on app load with security validation and auto-verify
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Basic token format validation (you can enhance this)
      if (token.length > 10 && !token.includes(' ')) {
        // Set loading state
        dispatch({ type: 'SET_LOADING', payload: true });
        
        // Auto-verify token (simulate what Login component does)
        const verifyToken = async () => {
          try {
            const response = await fetch(`${baseUrl}admin/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'token': token
              },
              body: JSON.stringify({ token })
            });
            
            const data = await response.json();
            
            if (data.status === 200) {
              // Token is valid, set auth data
              dispatch({ 
                type: 'SET_AUTH_SUCCESS', 
                payload: { data: data.data, token, group: data.data.group } 
              });
            } else {
              // Token is invalid
              console.warn('Token verification failed:', data.message);
              localStorage.removeItem("token");
              dispatch({ type: 'SET_AUTH_FAILURE' });
            }
          } catch (error) {
            console.error('Token verification error:', error);
            localStorage.removeItem("token");
            dispatch({ type: 'SET_AUTH_FAILURE' });
          }
        };
        
        verifyToken();
      } else {
        console.warn('Invalid token format detected, clearing storage');
        localStorage.removeItem("token");
        dispatch({ type: 'SET_AUTH_FAILURE' });
      }
    } else {
      dispatch({ type: 'SET_AUTH_FAILURE' });
    }
  }, []);

  // Security: Auto-logout on window focus (checks for token manipulation)
  useEffect(() => {
    const handleFocus = () => {
      const currentToken = localStorage.getItem("token");
      if (auth.isAuthenticated && !currentToken) {
        console.warn('Token was removed externally, logging out');
        handleLogout();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [auth.isAuthenticated, handleLogout]);
  // Modern secure auth data setter using useCallback for performance
  const setAuthData = useCallback(({ data, token, group }: any) => {
    if (!data || !token) {
      console.error('Invalid auth data provided');
      dispatch({ type: 'SET_AUTH_FAILURE' });
      return;
    }

    // Store token securely
    localStorage.setItem("token", token);
    
    // Use reducer for state management instead of multiple localStorage items
    dispatch({ 
      type: 'SET_AUTH_SUCCESS', 
      payload: { data, token, group } 
    });
  }, []);

  const triggerRefresh = useCallback(() => {
    setInputRefresh((prev: number) => prev + 1);
  }, []);

  // Modern helper functions using useCallback for performance
  const getUserRole = useCallback((): string | null => {
    return auth.data?.role || auth.data?.group || null;
  }, [auth.data]);

  const hasRole = useCallback((requiredRole: string): boolean => {
    const userRole = getUserRole();
    return userRole === requiredRole;
  }, [getUserRole]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    const userRole = getUserRole();
    return userRole ? roles.includes(userRole) : false;
  }, [getUserRole]);

  const isAuthenticated = useCallback((): boolean => {
    return auth.isAuthenticated && !!auth.token && !!auth.data;
  }, [auth.isAuthenticated, auth.token, auth.data]);

  const getUserData = useCallback(() => {
    return auth.data;
  }, [auth.data]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    auth,
    setAuthData,
    handleLogout,
    computeId,
    setComputeId,
    draftLca,
    setDraftLca,
    error,
    setError,
    triggerRefresh,
    inputRefresh,
    // Modern helper functions
    getUserRole,
    hasRole,
    hasAnyRole,
    isAuthenticated,
    getUserData,
    // Legacy compatibility (if needed)
    setAuth: setAuthData // For backward compatibility if any components still use setAuth
  }), [
    auth,
    setAuthData,
    handleLogout,
    computeId,
    draftLca,
    error,
    inputRefresh,
    getUserRole,
    hasRole,
    hasAnyRole,
    isAuthenticated,
    getUserData
  ]);

  return (
    <authContext.Provider value={contextValue}>
      {children}
    </authContext.Provider>
  );
};

export default AuthProvider;
